import { HelpSquare, ImageX } from '@untitledui/icons'
import { useMemo } from 'react'

import FadeTransition from '@components/FadeTransition.tsx'
import TransformableImage from '@components/productImage/TransformableImage'
import { transformImageUrl } from '@components/productImage/data'
import Facing from '@components/shelfDetails/Facing'
import ShelfContainer from '@components/shelfDetails/ShelfContainer'
import { useShelfHoverState } from '@components/shelfDetails/utils'
import { useDeferredLoading } from '@hooks/useDeferredLoading'
import { usePreloadImages } from '@hooks/usePreloadImages'
import { ProductWrapper, useProductsFromShelf } from '@state/product'
import { useShelf } from '@state/shelf/queries'
import { cx } from '@utils/cx.ts'

import type { FacingProducer } from './ShelfDetails'

interface ShelfProps {
  shelfId: string
  facingProducer?: FacingProducer
}

function ProductViewShelf({ shelfId, facingProducer }: ShelfProps) {
  const { data: shelf, isLoading } = useShelf(shelfId)
  const { products: shelfProducts, isLoading: areShelfProductsLoading } = useProductsFromShelf(shelfId)
  const { hoverState, onPointerMove, onPointerLeave } = useShelfHoverState()

  const imageUrls = useMemo(() => {
    if (shelfProducts.length === 0) return []

    return shelfProducts
      .map((product) => product.imageUrl)
      .filter((url): url is string => Boolean(url))
      .map((url) => transformImageUrl(url, 'shelfProductImage'))
  }, [shelfProducts])

  const areImagesPreLoading = !usePreloadImages(areShelfProductsLoading ? undefined : imageUrls)

  const shouldShowLoadingIndicator = useDeferredLoading(areShelfProductsLoading || areImagesPreLoading)

  if (isLoading || !shelf) {
    return <div className='size-full animate-pulse bg-gray-400'/>
  }

  const aspectRatio = shelf.width / shelf.height

  function renderImgForProduct(product?: ProductWrapper) {
    if (!product) return <HelpSquare className='mb-2 text-gray-400' />
    if (!product.imageUrl) return <ImageX className='mb-2 text-gray-400' />

    return (
      <TransformableImage
        type="shelfProductImage"
        src={product.imageUrl}
        className='max-h-full max-w-full animate-fade-in'
        style={{ mixBlendMode: 'multiply' }}
      />
    )
  }

  const shouldShowProducts = !areShelfProductsLoading && !areImagesPreLoading

  return (
    <ShelfContainer
      aspectRatio={aspectRatio}
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
    >
      { // Render layer from top to bottom by reversing a copied array
        shelf.layers.slice().reverse().map(((layer, i) => (
          <div
            key={layer.id}
            data-testid={`shelf:layer:${layer.id}`}
            className='absolute w-full'
            style={{
              bottom: `${((shelf.layers.length - 1 - i) / shelf.layers.length) * 100}%`,
              height: `${100 * (1 / shelf.layers.length)}%`,
            }}
          >
            {/* Shelf bar with shadow gradient below it */}
            <div className='absolute bottom-0 left-0 z-10 h-3 w-full bg-primary shadow-md' />
            <FadeTransition show={shouldShowLoadingIndicator} openDelay={150} closeDelay={50}>
              <div className='absolute inset-x-0 bottom-4 h-9 animate-pulse rounded-sm bg-gray-300' />
            </FadeTransition>
            { /* Render facings from left to right by reversing a copied array */ }
            {shouldShowProducts && layer.facings.slice().reverse().map((facing) => {
              const product = shelfProducts.find(p => p.gtin === facing.gtin)
              const state = !hoverState ? 'default' : hoverState === facing.id ? 'hovered' : 'dimmed'

              return (
                <Facing
                  key={facing.id}
                  className={cx(
                    'bottom-[12px] flex items-end justify-center',
                    facingProducer?.({ viewType: 'product', facing, product, state })
                  )}
                  facing={facing}
                  product={product}
                  state={state}
                >
                  {renderImgForProduct(product)}
                </Facing>
              )
            })}
          </div>
        )))
      }
    </ShelfContainer>
  )
}

export default ProductViewShelf